import { useState, useEffect, useCallback, useRef } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
  subscribePlants,
  addPlant as addPlantService,
  updatePlant as updatePlantService,
  waterPlant as waterPlantService,
  undoWaterPlant as undoWaterPlantService,
  deletePlant as deletePlantService,
  getPlantCount,
  updatePlantCareInfo,
  updatePlantCareInfoStatus,
  updatePlantSortOrders,
} from '../services/plants';
import { generateCareInfo } from '../services/api';
import { parseWateringInterval } from '../utils/wateringSchedule';
import type { PlantWithId, CreatePlantInput, UpdatePlantInput, CareInfo } from '../types/plant';



interface GenerateCareInfoResult {
  success: boolean;
  error?: string;
  resetIn?: number;
}

interface WaterPlantResult {
  success: boolean;
  error?: string;
  previousLastWatered?: Timestamp | null;
}

interface UsePlantsReturn {
  plants: PlantWithId[];
  loading: boolean;
  error: string | null;
  plantCount: number;
  canAddPlant: boolean;
  addPlant: (plantData: CreatePlantInput) => Promise<{ success: boolean; id?: string; error?: string }>;
  updatePlant: (plantId: string, updates: UpdatePlantInput) => Promise<{ success: boolean; error?: string }>;
  waterPlant: (plantId: string) => Promise<WaterPlantResult>;
  undoWaterPlant: (plantId: string, previousLastWatered: Timestamp | null) => Promise<{ success: boolean; error?: string }>;
  deletePlant: (plantId: string) => Promise<{ success: boolean; error?: string }>;
  getPlantById: (plantId: string) => PlantWithId | undefined;
  refreshPlantCount: () => Promise<void>;
  reorderPlants: (orderedIds: string[]) => Promise<{ success: boolean; error?: string }>;
  triggerCareInfoGeneration: (plantId: string, scientificName: string, commonName: string) => Promise<GenerateCareInfoResult>;
}

export function usePlants(): UsePlantsReturn {
  const { user } = useAuth();
  const [plants, setPlants] = useState<PlantWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plantCount, setPlantCount] = useState(0);

  // During beta, allow unlimited plants (keep FREE_TIER_LIMIT for post-beta)
  const canAddPlant = true;

  // Subscribe to plants on mount
  useEffect(() => {
    if (!user) {
      setPlants([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribePlants(
      user.uid,
      (plantsData) => {
        setPlants(plantsData);
        setPlantCount(plantsData.length);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Lazy migration: backfill wateringIntervalDays from care info for existing plants
  const migratedIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!user || plants.length === 0) return;

    for (const plant of plants) {
      // Skip if already has interval, already attempted, or no care info
      if (plant.wateringIntervalDays != null) continue;
      if (migratedIds.current.has(plant.id)) continue;
      if (!plant.careInfo) continue;

      migratedIds.current.add(plant.id);

      const interval = parseWateringInterval(plant.careInfo);
      if (interval != null) {
        // Fire-and-forget write — subscription will update local state
        updatePlantService(user.uid, plant.id, {
          wateringIntervalDays: interval,
          wateringAdjustmentDays: 0,
          autoAdjust: true,
        });
      }
    }
  }, [user, plants]);

  // Refresh plant count (for checking limits)
  const refreshPlantCount = useCallback(async () => {
    if (!user) return;
    const count = await getPlantCount(user.uid);
    setPlantCount(count);
  }, [user]);

  // Add a new plant
  const addPlant = useCallback(
    async (plantData: CreatePlantInput) => {
      if (!user) {
        return { success: false, error: 'Not signed in' };
      }

      const result = await addPlantService(user.uid, plantData);
      if (result.success) {
        return { success: true, id: result.id };
      }
      return { success: false, error: result.error };
    },
    [user]
  );

  // Update a plant
  const updatePlant = useCallback(
    async (plantId: string, updates: UpdatePlantInput) => {
      if (!user) {
        return { success: false, error: 'Not signed in' };
      }

      const result = await updatePlantService(user.uid, plantId, updates);
      if (result.success) {
        return { success: true };
      }
      return { success: false, error: result.error };
    },
    [user]
  );

  // Water a plant (with optimistic update)
  const waterPlant = useCallback(
    async (plantId: string): Promise<WaterPlantResult> => {
      if (!user) {
        return { success: false, error: 'Not signed in' };
      }

      // Get current plant to capture previous lastWatered for undo
      const currentPlant = plants.find((p) => p.id === plantId);
      const previousLastWatered = currentPlant?.lastWatered ?? null;

      // Optimistic update
      setPlants((prev) =>
        prev.map((plant) =>
          plant.id === plantId
            ? { ...plant, lastWatered: { toDate: () => new Date() } as PlantWithId['lastWatered'] }
            : plant
        )
      );

      const result = await waterPlantService(user.uid, plantId);
      if (!result.success) {
        // Revert on failure - the subscription will update with correct data anyway
        return { success: false, error: result.error };
      }
      return { success: true, previousLastWatered };
    },
    [user, plants]
  );

  // Undo a water action
  const undoWaterPlant = useCallback(
    async (plantId: string, previousLastWatered: Timestamp | null) => {
      if (!user) {
        return { success: false, error: 'Not signed in' };
      }

      // Optimistic update - revert to previous value
      setPlants((prev) =>
        prev.map((plant) =>
          plant.id === plantId
            ? { ...plant, lastWatered: previousLastWatered }
            : plant
        )
      );

      const result = await undoWaterPlantService(user.uid, plantId, previousLastWatered);
      if (!result.success) {
        return { success: false, error: result.error };
      }
      return { success: true };
    },
    [user]
  );

  // Delete a plant
  const deletePlant = useCallback(
    async (plantId: string) => {
      if (!user) {
        return { success: false, error: 'Not signed in' };
      }

      const result = await deletePlantService(user.uid, plantId);
      if (result.success) {
        return { success: true };
      }
      return { success: false, error: result.error };
    },
    [user]
  );

  // Get a plant by ID
  const getPlantById = useCallback(
    (plantId: string) => {
      return plants.find((plant) => plant.id === plantId);
    },
    [plants]
  );

  // Reorder plants
  const reorderPlants = useCallback(
    async (orderedIds: string[]) => {
      if (!user) return { success: false, error: 'Not signed in' };
      const orders = orderedIds.map((id, index) => ({ id, sortOrder: index }));

      // Optimistic update
      setPlants((prev) => {
        const map = new Map(prev.map((p) => [p.id, p]));
        return orderedIds.map((id) => map.get(id)!).filter(Boolean);
      });

      return updatePlantSortOrders(user.uid, orders);
    },
    [user]
  );

  // Trigger care info generation for a plant
  const triggerCareInfoGeneration = useCallback(
    async (plantId: string, scientificName: string, commonName: string): Promise<GenerateCareInfoResult> => {
      if (!user) {
        return { success: false, error: 'Not signed in' };
      }

      // Set status to generating
      await updatePlantCareInfoStatus(user.uid, plantId, 'generating');

      try {
        // Call the API - throws on error
        const result = await generateCareInfo({
          scientificName,
          commonName,
        });

        // Update plant with care info
        const updateResult = await updatePlantCareInfo(user.uid, plantId, result.careInfo as CareInfo);
        if (updateResult.success) {
          return { success: true };
        }
        // Failed to save, set error status
        await updatePlantCareInfoStatus(user.uid, plantId, 'error', 'Could not save care info.');
        return { success: false, error: 'Could not save care info.' };
      } catch (error) {
        // API call failed, set error status
        const errorMsg = error instanceof Error ? error.message : 'Failed to generate care info.';
        await updatePlantCareInfoStatus(user.uid, plantId, 'error', errorMsg);
        return { success: false, error: errorMsg };
      }
    },
    [user]
  );

  return {
    plants,
    loading,
    error,
    plantCount,
    canAddPlant,
    addPlant,
    updatePlant,
    waterPlant,
    undoWaterPlant,
    deletePlant,
    getPlantById,
    refreshPlantCount,
    reorderPlants,
    triggerCareInfoGeneration,
  };
}
